import type {
  AppSpec,
  AppStruct,
  NotificationIntegrationSpec,
  PerkIntegrationSpec,
  TriggerIntegrationSpec,
} from '@cohort/shared/schema/apps/app';
import {CohortEmailNotificationIntegrationSpec} from '@cohort/shared/schema/apps/cohort/notifications/email';
import {CohortFormPerkIntegrationSpec} from '@cohort/shared/schema/apps/cohort/perks/form';
import {CohortQrCodePerkIntegrationSpec} from '@cohort/shared/schema/apps/cohort/perks/qrCode';
import {CohortDigitalAssetOwnedTriggerIntegrationSpec} from '@cohort/shared/schema/apps/cohort/triggers/digitalAssetOwned';
import {CohortPerkUsedTriggerIntegrationSpec} from '@cohort/shared/schema/apps/cohort/triggers/perkUsed';
import {CohortVisitLinkTriggerIntegrationSpec} from '@cohort/shared/schema/apps/cohort/triggers/visitLink';
import {CohortAppConnectedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/appConnected';
import {CohortAppDisconnectedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/appDisconnected';
import {CohortDigitalAssetReceivedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/digitalAsset/digitalAssetReceived';
import {CohortDigitalAssetTransferredEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/digitalAsset/digitalAssetTransferred';
import {CohortJourneyCompletedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/journey/journeyCompleted';
import {CohortJourneyOptedInEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/journey/journeyOptedIn';
import {CohortJourneyStepCompletedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/journey/journeyStepCompleted';
import {CohortNewJourneyAvailableEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/journey/newJourneyAvailable';
import {CohortNewJourneyStepAvailableEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/journey/newJourneyStepAvailable';
import {CohortPerkReceivedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/perk/perkReceived';
import {CohortPerkUsedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/perk/perkUsed';
import {CohortUsernameChangedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/usernameChanged';
import {CohortXpsVisitedEventSpec} from '@cohort/shared/schema/apps/cohort/userEvents/xpsVisited';
import type {UserEventSpec, UserEventStruct} from '@cohort/shared/schema/apps/userEvent';

export const CohortPerkIntegrationSpecs = [
  CohortQrCodePerkIntegrationSpec,
  CohortFormPerkIntegrationSpec,
] as const satisfies ReadonlyArray<PerkIntegrationSpec>;
type CohortPerkIntegrationIds = (typeof CohortPerkIntegrationSpecs)[number]['id'];

export const CohortTriggerIntegrationSpecs = [
  CohortDigitalAssetOwnedTriggerIntegrationSpec,
  CohortPerkUsedTriggerIntegrationSpec,
  CohortVisitLinkTriggerIntegrationSpec,
] as const satisfies ReadonlyArray<TriggerIntegrationSpec>;
type CohortTriggerIntegrationIds = (typeof CohortTriggerIntegrationSpecs)[number]['id'];

export const CohortNotificationIntegrationSpecs = [
  CohortEmailNotificationIntegrationSpec,
] as const satisfies ReadonlyArray<NotificationIntegrationSpec>;
type CohortNotificationIntegrationIds = (typeof CohortNotificationIntegrationSpecs)[number]['id'];

export const CohortEventSpecs = [
  CohortAppConnectedEventSpec,
  CohortAppDisconnectedEventSpec,
  CohortDigitalAssetReceivedEventSpec,
  CohortDigitalAssetTransferredEventSpec,
  CohortJourneyCompletedEventSpec,
  CohortNewJourneyStepAvailableEventSpec,
  CohortJourneyOptedInEventSpec,
  CohortJourneyStepCompletedEventSpec,
  CohortNewJourneyAvailableEventSpec,
  CohortPerkReceivedEventSpec,
  CohortPerkUsedEventSpec,
  CohortUsernameChangedEventSpec,
  CohortXpsVisitedEventSpec,
] as const satisfies ReadonlyArray<UserEventSpec<CohortUserEventStruct>>;
type CohortEventIds = (typeof CohortEventSpecs)[number]['id'];

export type CohortAppStruct = AppStruct<
  'cohort',
  null,
  null,
  null,
  null,
  never,
  CohortNotificationIntegrationIds,
  CohortPerkIntegrationIds,
  CohortTriggerIntegrationIds,
  CohortEventIds
>;

export const CohortAppSpec: AppSpec<CohortAppStruct> = {
  id: 'cohort',
  name: 'Cohort',
  appCredentialsSchema: null,
  merchantConnector: null,
  userConnector: null,
  usersSyncSpec: null,
  actionSpecs: [],
  notificationIntegrationSpecs: CohortNotificationIntegrationSpecs,
  perkIntegrationSpecs: CohortPerkIntegrationSpecs,
  triggerIntegrationSpecs: CohortTriggerIntegrationSpecs,
  userEventSpecs: CohortEventSpecs,
};

type CohortEventResource = 'perk' | 'journey' | 'store' | 'digital-asset' | 'user' | 'app';
type CohortUserEventId = `cohort.${CohortEventResource}.${string}`;
export type CohortUserEventStruct = UserEventStruct<CohortUserEventId, unknown>;
